import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { shadow } from '../../styles/box';

import DropdownLabel from './DropdownLabel';
import { media } from '../../theme/media';
import TextRenderer from '../TextRenderer';

export const Wrapper = styled.div`
  max-height: 0;
  pointer-events: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: max-height 0s linear 150ms, opacity 150ms ease-in;
  min-width: max-content;
  max-width: ${rem(215)};
  z-index: -1;

  ${({ width }) => width !== 'small' && `
    position: fixed;
    top: ${rem(90)};
    width: max-content;
    min-width: 0;
    max-width: ${rem(700)};
    left: 50%;
    transform: translateX(-50%);
    ${media('>nav')} {
      max-width: ${rem(1080)};
    }
  `}

  ${({ align }) => align === 'right' && `
    left: auto;
    right: ${rem(-20)};
    transform: none;
  `}
`;

const Container = styled.div`
  padding: ${rem(12)};
  border: 1px solid ${colors.gray['200']};
  background: ${colors.gray['50']};
  color: ${colors.gray['500']};
  border-radius: 0 0 ${rem(12)} ${rem(12)};

  &::before {
    ${shadow}
    content: '';
    position: absolute;
    inset: 0;
    background: ${colors.white};
    border-radius: 0 0 ${rem(10)} ${rem(10)};
    line-height: 1.2;
    z-index: -1;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background: ${colors.blue['750']};
  `}
  }
`;

const InnerWrapper = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border: 1px solid ${colors.gray['200']};
  border-radius: ${rem(8)};
  background: ${colors.white};
`;

const DropdownPanel = forwardRef(({
  label,
  hideLabel = false,
  open = false,
  width = 'small',
  align = 'center',
  role,
  children,
  className
}, ref) => (
  <Wrapper visible={open} width={width} ref={ref} align={align} className={className}>
    <Container
      role={role}
      width={width}
      hideLabel={hideLabel}
      align={align}
    >
      {hideLabel ? (
        children
      ) : (
        <InnerWrapper>
          <DropdownLabel>
            <TextRenderer text={label} />
          </DropdownLabel>
          {children}
        </InnerWrapper>
      )}
    </Container>
  </Wrapper>
));

DropdownPanel.propTypes = {
  label: PropTypes.string,
  hideLabel: PropTypes.bool,
  open: PropTypes.bool,
  width: PropTypes.oneOf(['small', 'medium', 'large']),
  align: PropTypes.oneOf(['center', 'right']),
  role: PropTypes.string,
  children: PropTypes.node
};

export default DropdownPanel;
